import axios from 'axios';

import { handleApiSucess, handleApiError, getRequestHeaders } from 'api/util.api';

import { createRoute } from 'lib/util';
import { handleRequest } from './util.api';

export const getAssetsCSVUrl = (cId, sId) => `/companies/${cId}/subcompanies/${sId}/reports/assets`;

/**
 * @author Jorge Casas
 * @creationDate 07/04/2024
 *
 * @description Get the URL to download the assets report in XLSX format for a company and subcompany
 *
 * @param {string} cId - Company ID
 * @param {string} sId - Subcompany ID
 * @returns {string} - URL to download the assets report in XLSX format
 * @version 3.4.6
 */
export const getAssetsXLSXReportUrl = (cId, sId) =>
  `/companies/${cId}/subcompanies/${sId}/reports/assets/xlsx`;

export const getDepreciationReportPaginatedUrl = (cId, sId) =>
  createRoute(cId, sId, `reports/depreciation`);

export const getDepreciationReportCSVUrl = (cId, sId) =>
  createRoute(cId, sId, `reports/depreciation/export`);

/**
 * @author Jorge Casas
 *
 * @description Get the URL to download the depreciation assets report in XLSX format for a company and subcompany
 *
 * @param {string} cId - Company ID
 * @param {string} sId - Subcompany ID
 * @returns {string} - URL to download the assets report in XLSX format
 * @version 3.4.4
 */
export const getDepreciationReportXLSXUrl = (cId, sId) =>
  createRoute(cId, sId, `reports/depreciation/export-xlsx`);

export const getDepreciationReportArrayUrl = (cId, sId) =>
  createRoute(cId, sId, `reports/depreciation/export_array`);

export const getDisabledReportPaginatedUrl = (cId, sId) =>
  createRoute(cId, sId, `reports/disabled`);

export const getDisabledReportXLSXUrl = (cId, sId) =>
  createRoute(cId, sId, `reports/disabled/export-xlsx`);

export const getDisabledReportCSVUrl = (cId, sId) =>
  createRoute(cId, sId, `reports/disabled/export`);

export const getDisabledReportArrayUrl = (cId, sId) =>
  createRoute(cId, sId, `reports/disabled/export_array`);

export const getEmployeesAssetsReportUrl = (cId, sId) =>
  createRoute(cId, sId, `reports/employees_assets`);

export const getHistoricsReportPaginatedUrl = (cId, sId) =>
  createRoute(cId, sId, `reports/historic`);

export const getHistoricsReportCSVUrl = (cId, sId) =>
  createRoute(cId, sId, `reports/historic/export`);

/**
 * @author Jorge Casas
 * 
 * @description Get the URL to download the historic report in XLSX format for a company and subcompany
 * 
 * @param {string} cId - Company ID
 * @param {string} sId - Subcompany ID
 * @returns {string} - URL to download the historic report in XLSX format
 * @version 3.4.8
 */
export const getHistoricsReportXLSXUrl = (cId, sId) =>
  createRoute(cId, sId, `reports/historic/export-xlsx`);

export const getHistoricsReportArrayUrl = (cId, sId) =>
  createRoute(cId, sId, `reports/historic/export_array`);

const service = {
  configure(endpoint = '', auth = {}, defaultHeaders = {}) {
    this.endpoint = endpoint;
    this.auth = auth;
    this.defaultHeaders = defaultHeaders;
    this.getHeaders = getRequestHeaders(this.auth, this.defaultHeaders);

    this.axios = axios.create({
      baseURL: this.endpoint,
      timeout: 30000,
    });
    this.axios.interceptors.request.use((request) => {
      handleRequest(request);
      return request;
    });

    return this;
  },

  getAssetsCSV(companyId, subcompanyId, filters) {
    const url = getAssetsCSVUrl(companyId, subcompanyId);
    return this.axios
      .get(url, { params: { ...filters }, ...this.getHeaders() })
      .then(handleApiSucess)
      .catch(handleApiError);
  },

  /**
   * @author Jorge Casas
   *
   * @description Get assets report in XLSX format for a company and subcompany with filters applied
   *
   * @param {string} companyId - Company ID
   * @param {string} subcompanyId - Subcompany ID
   * @param {object} filters - Filters to apply to the report
   * @returns {Promise} - Promise object represents the XLSX file with the assets report
   * 
   * @version 3.4.6
   */
  getAssetsXLSX(companyId, subcompanyId, filters) {
    const url = getAssetsXLSXReportUrl(companyId, subcompanyId);
    return this.axios
      .get(url, { params: { ...filters }, ...this.getHeaders() })
      .then(handleApiSucess)
      .catch(handleApiError);
  },

  getDepreciationReportPaginated(companyId, subcompanyId, filters, limit, page, orderBy, order) {
    const url = getDepreciationReportPaginatedUrl(companyId, subcompanyId);
    const params = { ...filters, limit, page, orderBy, order };
    return this.axios
      .get(url, { ...this.getHeaders(), params })
      .then(handleApiSucess)
      .catch(handleApiError);
  },

  getDepreciationReport(companyId, subcompanyId, filters) {
    const url = getDepreciationReportCSVUrl(companyId, subcompanyId);
    const params = { ...filters };
    return this.axios
      .get(url, { ...this.getHeaders(), params })
      .then(handleApiSucess)
      .catch(handleApiError);
  },

  /**
   * @author Jorge Casas
   * @creationDate 27/04/2024
   *
   * @description Get the depreciation assets report in XLSX format for a company and subcompany
   */
  getDepreciationReportXLSX(companyId, subcompanyId, filters) {
    const url = getDepreciationReportXLSXUrl(companyId, subcompanyId);
    const params = { ...filters };
    return this.axios
      .get(url, { ...this.getHeaders(), params })
      .then(handleApiSucess)
      .catch(handleApiError);
  },

  getDepreciationReportArray(companyId, subcompanyId, filters) {
    const url = getDepreciationReportArrayUrl(companyId, subcompanyId);
    const params = { ...filters };
    return this.axios
      .get(url, { ...this.getHeaders(), params })
      .then(handleApiSucess)
      .catch(handleApiError);
  },

  getDisabledReportPaginated(companyId, subcompanyId, filters, limit, page, orderBy, order) {
    const url = getDisabledReportPaginatedUrl(companyId, subcompanyId);
    const params = { ...filters, limit, page, orderBy, order };
    return this.axios
      .get(url, { ...this.getHeaders(), params })
      .then(handleApiSucess)
      .catch(handleApiError);
  },

  /**
   * @author Jorge Casas
   * 
   * @function getDisabledReportXLSX
   * 
   * @description This function is used to get the disabled report in XLSX format
   * 
   * @param {number} companyId
   * @param {number} subcompanyId
   * @param {object} filters
   * @returns {object}
   * @version 3.4.6
   */
  getDisabledReportXLSX(companyId, subcompanyId, filters) {
    const url = getDisabledReportXLSXUrl(companyId, subcompanyId);
    const params = { ...filters };
    return this.axios
      .get(url, { ...this.getHeaders(), params })
      .then(handleApiSucess)
      .catch(handleApiError);
  },

  getDisabledReportCSV(companyId, subcompanyId, filters) {
    const url = getDisabledReportCSVUrl(companyId, subcompanyId);
    const params = { ...filters };
    return this.axios
      .get(url, { ...this.getHeaders(), params })
      .then(handleApiSucess)
      .catch(handleApiError);
  },

  getDisabledReportArray(companyId, subcompanyId, filters) {
    const url = getDisabledReportArrayUrl(companyId, subcompanyId);
    const params = { ...filters };
    return this.axios
      .get(url, { ...this.getHeaders(), params })
      .then(handleApiSucess)
      .catch(handleApiError);
  },

  getEmployeesAssetsReport(companyId, subcompanyId, filters, limit, page, orderBy, order) {
    const url = getEmployeesAssetsReportUrl(companyId, subcompanyId);
    const params = { ...filters, limit, page, orderBy, order };
    return this.axios
      .get(url, { ...this.getHeaders(), params })
      .then(handleApiSucess)
      .catch(handleApiError);
  },

  getHistoricsReport(companyId, subcompanyId, filters, limit, page, orderBy, order) {
    const url = getHistoricsReportPaginatedUrl(companyId, subcompanyId);
    const params = { ...filters, limit, page, orderBy, order };
    return this.axios
      .get(url, { ...this.getHeaders(), params })
      .then(handleApiSucess)
      .catch(handleApiError);
  },
  getHistoricsReportPaginated(companyId, subcompanyId, filters, limit, page, orderBy, order) {
    const url = getHistoricsReportPaginatedUrl(companyId, subcompanyId);
    const params = { ...filters, limit, page, orderBy, order };
    return this.axios
      .get(url, { ...this.getHeaders(), params })
      .then(handleApiSucess)
      .catch(handleApiError);
  },

  getHistoricsReportCSV(companyId, subcompanyId, filters) {
    const url = getHistoricsReportCSVUrl(companyId, subcompanyId);
    const params = { ...filters };
    return this.axios
      .get(url, { ...this.getHeaders(), params })
      .then(handleApiSucess)
      .catch(handleApiError);
  },

  /**
   * @author Jorge Casas
   * 
   * @function getHistoricsReportXLSX
   * 
   * @description This function is used to get the historics report in XLSX format
   * 
   * @param {number} companyId
   * @param {number} subcompanyId
   * @param {object} filters
   * @returns {object}
   * @version 3.4.8
   */
  getHistoricsReportXLSX(companyId, subcompanyId, filters) {
    const url = getHistoricsReportXLSXUrl(companyId, subcompanyId);
    const params = { ...filters };
    return this.axios
      .get(url, { ...this.getHeaders(), params })
      .then(handleApiSucess)
      .catch(handleApiError);
  },

  getHistoricsReportArray(companyId, subcompanyId, filters) {
    const url = getHistoricsReportArrayUrl(companyId, subcompanyId);
    const params = { ...filters };
    return this.axios
      .get(url, { ...this.getHeaders(), params })
      .then(handleApiSucess)
      .catch(handleApiError);
  },
};

export default service;



// WEBPACK FOOTER //
// src/lib/api/reports.api.js